import { useCallback, useEffect, useRef, useState } from 'react';
import { StudioDetailView } from '@/components/StudioDetailView';
import { AppDrawer } from '@/components/ui/AppDrawer';
import type { Studio } from '@/types';

const STUDIO_DRAWER_TITLE_ID = 'studio-drawer-title';
const DRAWER_CONTENT_CLEAR_DELAY_MS = 240;

interface StudioDrawerProps {
  isOpen: boolean;
  studio: Studio | null;
  onClose: () => void;
  aiIdPrefix?: string;
}

export function StudioDrawer({ isOpen, studio, onClose, aiIdPrefix = 'studio-drawer' }: StudioDrawerProps) {
  const closeHandlerRef = useRef<(() => void) | null>(null);
  const [renderedStudio, setRenderedStudio] = useState<Studio | null>(studio);
  const canOpen = isOpen && studio !== null;

  useEffect(() => {
    if (canOpen) {
      setRenderedStudio(studio);
      return undefined;
    }

    closeHandlerRef.current = null;
    const timeoutId = window.setTimeout(() => setRenderedStudio(null), DRAWER_CONTENT_CLEAR_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [canOpen, studio]);

  const registerCloseHandler = useCallback((handler: (() => void) | null) => {
    closeHandlerRef.current = handler;
  }, []);

  const handleRequestClose = useCallback(() => {
    if (closeHandlerRef.current) {
      closeHandlerRef.current();
      return;
    }

    onClose();
  }, [onClose]);

  if (!canOpen && renderedStudio === null) {
    return null;
  }

  return (
    <AppDrawer
      open={canOpen}
      onRequestClose={handleRequestClose}
      titleId={STUDIO_DRAWER_TITLE_ID}
      aiIdPrefix={aiIdPrefix}
    >
      {renderedStudio ? (
        <StudioDetailView
          studio={renderedStudio}
          titleId={STUDIO_DRAWER_TITLE_ID}
          onRequestClose={handleRequestClose}
          onClose={onClose}
          registerCloseHandler={registerCloseHandler}
          aiIdPrefix={`${aiIdPrefix}-detail-view`}
        />
      ) : null}
    </AppDrawer>
  );
}
